import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { BnInternalHeaders, ENV_CONFIG } from '@bn/primitives';

// ── Types (standalone — not imported from the lazy-loaded admin module) ──

export interface MatchedSurvey {
  id: string;
  name: string;
  display_type: 'modal' | 'popup';
  delay_seconds: number;
  coupon_code: string | null;
  auto_close: boolean;
  thank_you_text: string;
  steps: MatchedSurveyStep[];
}

export interface MatchedSurveyStep {
  id: string;
  title: string | null;
  description: string | null;
  position: number;
  fields: MatchedSurveyField[];
}

export interface MatchedSurveyField {
  id: string;
  label: string;
  description: string | null;
  field_type: 'rating' | 'input' | 'single_choice' | 'multi_choice';
  options: string[] | null;
  is_required: boolean;
  position: number;
}

export interface MatchSurveyResponse {
  survey: MatchedSurvey | null;
}

// Same as MatchedSurvey, plus the targeting fields the client needs to filter
// locally on every URL change.
export interface ActiveSurvey extends MatchedSurvey {
  url_pattern: string;
  device_type: 'all' | 'desktop' | 'tablet' | 'mobile';
}

export interface ActiveSurveysResponse {
  surveys: ActiveSurvey[];
}

export interface SurveyAnswer {
  field_id: string;
  value: string | string[];
}

// ── Service ──

@Injectable({ providedIn: 'root' })
export class SurveyWidgetService {
  #http = inject(HttpClient);
  #apiUrl = inject(ENV_CONFIG).surveysApiUrl;

  isAvailable(): boolean {
    return !!this.#apiUrl;
  }

  loadActiveSurveys(categoryType?: number, billingType?: number): Observable<ActiveSurveysResponse> {
    const params: Record<string, string> = {};
    if (categoryType !== undefined) params['category_type'] = String(categoryType);
    if (billingType !== undefined) params['billing_type'] = String(billingType);
    const headers = new HttpHeaders().set(BnInternalHeaders.IgnoreErrorToast, 'true');
    return this.#http.get<ActiveSurveysResponse>(`${this.#apiUrl}/surveys/active`, { params, headers });
  }

  matchSurvey(url: string, deviceType: string, categoryType?: number, billingType?: number): Observable<MatchSurveyResponse> {
    const params: Record<string, string> = { url, device_type: deviceType };
    if (categoryType !== undefined) params['category_type'] = String(categoryType);
    if (billingType !== undefined) params['billing_type'] = String(billingType);
    const headers = new HttpHeaders().set(BnInternalHeaders.IgnoreErrorToast, 'true');
    return this.#http.get<MatchSurveyResponse>(`${this.#apiUrl}/surveys/match`, { params, headers });
  }

  // Mirrors the server's URL pattern matcher: '*' wildcards over literal segments.
  matchesUrl(pattern: string, url: string): boolean {
    const segments = pattern.split('*');
    if (segments.length === 1) return url === pattern;

    let pos = 0;
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      if (seg === '') continue;
      if (i === 0) {
        if (!url.startsWith(seg)) return false;
        pos = seg.length;
        continue;
      }
      if (i === segments.length - 1) {
        return url.indexOf(seg, pos) === url.length - seg.length;
      }
      const idx = url.indexOf(seg, pos);
      if (idx === -1) return false;
      pos = idx + seg.length;
    }
    return true;
  }

  submitResponse(surveyId: string, answers: SurveyAnswer[]): Observable<{ success: boolean; response_id: string }> {
    return this.#http.post<{ success: boolean; response_id: string }>(`${this.#apiUrl}/surveys/${surveyId}/respond`, { answers });
  }

  dismissSurvey(surveyId: string): Observable<{ success: boolean }> {
    const headers = new HttpHeaders().set(BnInternalHeaders.IgnoreErrorToast, 'true');
    return this.#http.post<{ success: boolean }>(`${this.#apiUrl}/surveys/${surveyId}/dismiss`, {}, { headers });
  }

  getDeviceType(): string {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 992) return 'tablet';
    return 'desktop';
  }
}
